/**
 * File: crmTutorialsData.ts
 * Path: apps/admin/src/shared/utils/crmTutorialsData.ts
 * Description: The tutorials for the CRM pages: Today, Pipeline, Clients, a
 * business (lead or client), Projects, a project, Care plans, a care plan, Money,
 * an invoice, a contract and the CRM tabs of Settings. Button and tab names are the live
 * ones; the tutorial test checks every step's length and target.
 */

import { ADMIN_PATHS } from "@idk/config";
import { HEAD, PAGE_ACTIONS, type Tutorial } from "./tutorialTypes";

const P = ADMIN_PATHS;
const BUSINESS = P.businesses.detail(":id");
const PROJECT = "/projects/:clientId/:jobId";
const PLAN = "/care-plans/:id";
const INVOICE = "/money/invoices/:key";
const CONTRACT = "/contracts/:id";

export const CRM_TUTORIALS: Tutorial[] = [
  // Today
  {
    id: "today-read",
    title: "Read your day",
    hint: "4 steps",
    pages: [P.dashboard],
    group: "CRM: Today",
    steps: [
      {
        target: '[aria-label="Money"]',
        title: "Money in one line",
        text: "Owed to you is on invoices not paid yet. Still to bill is signed work with no invoice yet. Bank is what you have now. Press Overview for the books.",
      },
      {
        target: PAGE_ACTIONS,
        title: "New",
        text: "The New menu starts a lead, a quote or a contract from anywhere.",
      },
      {
        target: '[aria-label="Needs attention"]',
        title: "Do today",
        text: "Follow-ups, money to chase, unpaid deposits and care plans ending, most urgent first. Open one to act on it.",
      },
      {
        target: '[aria-label="Quotes in these dates"]',
        title: "Pick the dates",
        text: "The date button above these cards sets the dates for the quotes and clients. The rest of the page is as of today.",
      },
    ],
  },
  // Pipeline
  {
    id: "lead-add",
    title: "Add a lead",
    hint: "3 steps",
    pages: [P.leads.root],
    group: "CRM: Pipeline",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "New lead",
        text: "Press New lead. Type the business name and a phone number or an email. You can fill in the rest later.",
      },
      {
        target: '[aria-label="Pipeline figures"]',
        title: "The summary line",
        text: "Open pipeline, follow-ups due, deals going stale and what you won this month. Press the (i) beside a figure to see what it means.",
      },
      {
        target: '[aria-label="Deals"]',
        title: "Your deals",
        text: "The new lead shows up here, in the first stage. Press it to open its page.",
      },
    ],
  },
  {
    id: "pipeline-find",
    title: "Find a deal in the list",
    hint: "3 steps",
    pages: [P.leads.root],
    group: "CRM: Pipeline",
    steps: [
      {
        target: '[aria-label="Deals"]',
        title: "Search and filter",
        text: "Type a name to search. The Filters menu narrows the list by stage and where the lead came from. One button switches between Board and List.",
      },
      {
        target: '[aria-label="Deals"]',
        title: "Open a deal",
        text: "Press a row or a card to open that lead. Its notes, quotes and contracts are on its own page.",
      },
      {
        target: '[aria-label="Deals"]',
        title: "Won and lost",
        text: "Won and lost deals leave the board. Under it, press Clients to see the ones you won, or Filters to show every stage again.",
      },
    ],
  },
  {
    id: "leads-search-maps",
    title: "Find new leads on Google Maps",
    hint: "2 steps",
    pages: [P.leads.find],
    group: "CRM: Pipeline",
    steps: [
      {
        target: "main form",
        title: "Search",
        text: "Type the kind of business and the place. Ones without a website are usually the best leads for a new site.",
      },
      {
        target: HEAD,
        title: "Download the good ones",
        text: "Download the ones you want as Excel or CSV. Downloading does not add them to your pipeline.",
      },
    ],
  },
  // One lead
  {
    id: "lead-move",
    title: "Move a deal to the next step",
    hint: "2 steps",
    pages: [BUSINESS],
    group: "CRM: Pipeline",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "Stage and next step",
        text: "The stage menu moves the deal on. Won moves them to Clients with a client number and the same history. The big button is the next thing to do for this stage. More holds everything else.",
      },
      {
        target: '[aria-label="Business record"]',
        title: "Everything about them",
        text: "A lead has four tabs: Overview, Quotes and contracts, Notes and files, and Details (contacts, branches and the website audit). Log each call so you remember it.",
      },
    ],
  },
  {
    id: "business-duplicate",
    title: "Deal with a possible duplicate",
    hint: "2 steps",
    pages: [BUSINESS],
    group: "CRM: Pipeline",
    steps: [
      {
        target: '[data-tour="duplicate-banner"]',
        title: "Possible duplicate",
        text: "This box shows when another record has the same phone number. Press the name to look at it first.",
      },
      {
        target: '[data-tour="duplicate-banner"]',
        title: "Merge or Not the same",
        text: "Merge shows what will move. Type the newer record's name, then press Merge and remove. Not the same keeps both and stops the box coming back.",
      },
    ],
  },
  {
    id: "lead-follow-up",
    title: "Follow up a lead who has not answered",
    hint: "2 steps",
    pages: [BUSINESS],
    group: "CRM: Pipeline",
    steps: [
      {
        target: "#business-tab-overview",
        title: "Overview",
        text: "Open Overview. After the first message, the Follow-up card shows which call is next and on what day.",
      },
      {
        target: '[aria-label="Business record"]',
        title: "Log the call",
        text: "If they answered, move them to Talking and the calls stop. If not, log the unanswered call. After the last call the card says to let them go.",
      },
    ],
  },
  {
    id: "lead-quote-contract",
    title: "Send a quote or a contract",
    hint: "2 steps",
    pages: [BUSINESS],
    group: "CRM: Pipeline",
    steps: [
      {
        target: "#business-tab-sales",
        title: "Quotes and contracts",
        text: "Open this tab. It lists every quote and contract for this lead, and what each one is waiting for.",
      },
      {
        target: PAGE_ACTIONS,
        title: "The big button",
        text: "When you are talking, the big button makes a quote. After the quote goes out, it makes the contract to sign.",
      },
    ],
  },
  // Clients
  {
    id: "client-add",
    title: "Add a client",
    hint: "2 steps",
    pages: [P.clients.root],
    group: "CRM: Clients",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "New client",
        text: "Press New client for someone who already pays you. A lead who signs a contract becomes a client by themselves, so do not add them twice.",
      },
      {
        target: '[aria-label="Clients"]',
        title: "The list",
        text: "Search, filter, or press a row to open that client.",
      },
    ],
  },
  {
    id: "client-owed-list",
    title: "See who owes you on the Clients list",
    hint: "2 steps",
    pages: [P.clients.root],
    group: "CRM: Clients",
    steps: [
      {
        target: '[aria-label="Client figures"]',
        title: "One line at the top",
        text: "Owed to you is money on invoices you sent that is not paid yet. Still to bill is money in signed contracts you have not invoiced. Press the (i) beside a word to read what it means.",
      },
      {
        target: '[aria-label="Clients list"]',
        title: "Each client",
        text: "The Owed to you and Still to bill columns show the same two figures for each client. A red Late means an invoice is past its due date. The number of clients is under the list.",
      },
    ],
  },
  {
    id: "client-money",
    title: "See what a client owes",
    hint: "2 steps",
    pages: [BUSINESS],
    group: "CRM: Clients",
    steps: [
      {
        target: "#business-tab-money",
        title: "Money",
        text: "The line under the name shows Owed to you, Still to bill and Paid so far. Open Money for their quotes, contracts, invoices, payments and care plan.",
      },
      {
        target: '[data-tour="client-new-invoice"]',
        title: "New invoice",
        text: "On the Money tab, New invoice starts an invoice for this client. The More menu at the top holds New quote, New contract and New project.",
      },
    ],
  },
  {
    id: "client-history",
    title: "Find a client's notes and files",
    hint: "2 steps",
    pages: [BUSINESS],
    group: "CRM: Clients",
    steps: [
      {
        target: "#business-tab-notes",
        title: "Notes and files",
        text: "Open Notes and files. Every note, meeting and contract is listed with its date. Below it you can add a file and keep it with the client.",
      },
      {
        target: "#business-tab-details",
        title: "Details",
        text: "Open Details to fix their name, contacts, branches or registration number, and to see their consent. Invoices and contracts pick these up.",
      },
    ],
  },
  {
    id: "client-log-note",
    title: "Log a call or a note about a client",
    hint: "2 steps",
    pages: [BUSINESS],
    group: "CRM: Clients",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "Log a note",
        text: "Press Log a note, pick what happened, write it and save. WhatsApp and Call open a chat or a call and write a line in their history for you.",
      },
      {
        target: PAGE_ACTIONS,
        title: "Change their type",
        text: "Press More, then pick Client, Lead, Partner or Past client under Type. The line under their name shows the type.",
      },
    ],
  },
  {
    id: "client-delete",
    title: "Delete a client",
    hint: "2 steps",
    pages: [BUSINESS],
    group: "CRM: Clients",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "More",
        text: "Press More, then Delete client. It is for a client made by mistake. Their notes and projects go. Contracts, quotes and the books stay.",
      },
      {
        target: PAGE_ACTIONS,
        title: "Confirm with their name",
        text: "Paste the client's full name in the box (Copy name does it for you). The Delete client button stays off until the name matches exactly.",
      },
    ],
  },
  // Projects
  {
    id: "project-add",
    title: "Add a project",
    hint: "3 steps",
    pages: [P.projects.root],
    group: "CRM: Projects",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "New project",
        text: "Press New project and pick the client. A project is one job you build for them. A client can have many.",
      },
      {
        target: '[aria-label="Project figures"]',
        title: "The summary line",
        text: "What is in progress, still owed, your time this month and what needs attention. Press an (i) to see what a figure means.",
      },
      {
        target: '[aria-label="Projects"]',
        title: "The board",
        text: "Each job sits in a column for its stage. Press one to open its plan, time and money.",
      },
    ],
  },
  {
    id: "project-money-time",
    title: "Track a job's money and time",
    hint: "3 steps",
    pages: [PROJECT],
    group: "CRM: Projects",
    steps: [
      {
        target: "#project-tab-milestones",
        title: "Milestones and invoices",
        text: "Open this tab to see each payment this job is owed, and the invoice for it.",
      },
      {
        target: "#project-tab-time",
        title: "Time",
        text: "The hours you spent on this job. Add time from the menu in the page header, so the job shows what it costs you.",
      },
      {
        target: "#project-tab-approvals",
        title: "Approvals",
        text: "Open Approvals to see what the client has to say yes to, like a design or a change.",
      },
    ],
  },
  {
    id: "project-delete",
    title: "Delete a project",
    hint: "2 steps",
    pages: [PROJECT],
    group: "CRM: Projects",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "More",
        text: "Press More, then Delete project. Use it for a duplicate or a project made by mistake. Contracts, invoices and time already logged stay.",
      },
      {
        target: PAGE_ACTIONS,
        title: "Confirm with its name",
        text: "Paste the project's full name in the box (the copy button does it for you). Delete project stays off until the name matches exactly.",
      },
    ],
  },
  // Care plans
  {
    id: "care-plan-add",
    title: "Add a care plan",
    hint: "3 steps",
    pages: [P.carePlans.root],
    group: "CRM: Care plans",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "New care plan",
        text: "Press New care plan and pick the client. A care plan is a monthly payment to keep their site or app running.",
      },
      {
        target: '[aria-label="Care plan figures"]',
        title: "The summary line",
        text: "What plans pay each month, how many are active, how many were cancelled lately and the take-up. Press an (i) for what each means.",
      },
      {
        target: '[aria-label="Care plans"]',
        title: "The lists",
        text: "Press a plan to open it and see what it covers and what has been paid.",
      },
    ],
  },
  {
    id: "care-plan-renewals",
    title: "Find plans that end soon",
    hint: "1 step",
    pages: [P.carePlans.root],
    group: "CRM: Care plans",
    steps: [
      {
        target: '[role="tablist"][aria-label="Care plan lists"]',
        title: "Plans, Renewals, Support",
        text: "Plans lists who pays monthly. Renewals lists plans whose minimum term ends soon, so you can offer a new agreement. Support lists what clients have asked for.",
      },
    ],
  },
  {
    id: "care-plan-payments",
    title: "Check what a plan has paid",
    hint: "2 steps",
    pages: [PLAN],
    group: "CRM: Care plans",
    steps: [
      {
        target: "#plan-tab-payments",
        title: "Payments",
        text: "Open Payments to see what this plan has been paid, month by month.",
      },
      {
        target: "#plan-tab-support",
        title: "Support",
        text: "Open Support to see what this client has asked for under this plan.",
      },
    ],
  },
  {
    id: "care-plan-new-term",
    title: "Another 12 months, or month to month",
    hint: "1 step",
    pages: [PLAN],
    group: "CRM: Care plans",
    steps: [
      {
        target: '[data-tour="plan-new-agreement"]',
        title: "New 12-month agreement",
        text: "After the minimum term the plan runs month to month with nothing to sign. For another 12 months, press New 12-month agreement. The client signs it, it becomes a new plan, and you mark the old one Completed.",
      },
    ],
  },
  {
    id: "care-plan-end",
    title: "End a plan",
    hint: "1 step",
    pages: [PLAN],
    group: "CRM: Care plans",
    steps: [
      {
        target: '[data-tour="plan-status"]',
        title: "Completed or Cancelled",
        text: "Press Status. Pick Completed when the plan ran its term and is paid up. Pick Cancelled when the client stopped it early. Both stop new invoices; only Cancelled counts as a lost client.",
      },
    ],
  },
  // Money
  {
    id: "money-new-invoice",
    title: "Make an invoice",
    hint: "2 steps",
    pages: [P.money],
    group: "CRM: Money",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "New invoice",
        text: "Press New invoice, pick the contract and the payment, and check the preview on the right. The invoice gets its number when you issue it.",
      },
      {
        target: '[role="tablist"][aria-label="Money lists"]',
        title: "Or start from Still to bill",
        text: "Still to bill lists payments that are due and have no invoice yet. Make an invoice straight from a row.",
      },
    ],
  },
  {
    id: "money-lists",
    title: "See who has not paid",
    hint: "1 step",
    pages: [P.money],
    group: "CRM: Money",
    steps: [
      {
        target: '[role="tablist"][aria-label="Money lists"]',
        title: "Invoices, Still to bill, Agreements",
        text: "Invoices shows what you sent and who has paid. Still to bill shows payments with no invoice yet. Agreements shows the contracts behind them.",
      },
    ],
  },
  {
    id: "invoice-send",
    title: "Send an invoice",
    hint: "2 steps",
    pages: [INVOICE],
    group: "CRM: Money",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "The main button",
        text: "The big button is the next step: Issue it, then Post to ledger, then Send. The menu beside it holds the rest.",
      },
      {
        target: '[aria-label="Invoice history"]',
        title: "History",
        text: "Every step the invoice has been through is listed here with its day, so you know what happened and when.",
      },
    ],
  },
  // Contract
  {
    id: "contract-send",
    title: "Send a contract for signing",
    hint: "2 steps",
    pages: [CONTRACT],
    group: "CRM: Contracts",
    steps: [
      {
        target: '[data-tour="contract-terms"]',
        title: "Terms",
        text: "Check the terms first. A draft can still be edited. Once it is sent, the terms are frozen, and you withdraw it to change anything.",
      },
      {
        target: '[data-tour="contract-next"]',
        title: "Next step",
        text: "This card shows the one thing to do now, like sending the signing link on WhatsApp. The client signs on their phone.",
      },
    ],
  },
  {
    id: "contract-deal-terms",
    title: "Change the terms for one deal",
    hint: "2 steps",
    pages: [CONTRACT],
    group: "CRM: Contracts",
    steps: [
      {
        target: '[aria-label="Contract terms"]',
        title: "Open the terms",
        text: "While the contract is a draft, tick the kind of work under Scope. Press Terms for this deal below it and change the rounds, the ways to pay or the words.",
      },
      {
        target: '[aria-label="Contract terms"]',
        title: "Check the preview, then save",
        text: "The clauses in the preview follow your changes. Press Save draft. Once the contract is sent the terms are frozen. Press Reset to standard to undo.",
      },
    ],
  },
  {
    id: "contract-delete-archive",
    title: "Delete or archive a contract",
    hint: "2 steps",
    pages: [CONTRACT],
    group: "CRM: Contracts",
    steps: [
      {
        target: '[data-tour="contract-next"]',
        title: "Delete an unsigned one",
        text: "Open More and press Delete. The box lists what goes with it. Type the reference to confirm. The quote goes back to Sent.",
      },
      {
        target: '[data-tour="contract-next"]',
        title: "Archive a signed one",
        text: "A signed contract is kept for the record. Open More and press Archive. It leaves the lists. Press Unarchive to bring it back.",
      },
    ],
  },
  {
    id: "contracts-bulk",
    title: "Delete or archive several contracts",
    hint: "2 steps",
    pages: [P.money],
    group: "CRM: Contracts",
    steps: [
      {
        target: '[aria-label="Money lists"]',
        title: "Tick them",
        text: "Open Agreements and tick the contracts. A bar shows how many are selected, with Delete and Archive.",
      },
      {
        target: '[aria-label="Money lists"]',
        title: "Read the report",
        text: "Each contract gets a line saying what was done, or why it was kept. To see archived ones, press Filter and choose Show archived.",
      },
    ],
  },
  {
    id: "contract-check-copy",
    title: "Check a signed copy",
    hint: "2 steps",
    pages: [CONTRACT],
    group: "CRM: Contracts",
    steps: [
      {
        target: '[data-tour="contract-audit"]',
        title: "Audit trail",
        text: "Everything that happened to the contract, kept by the server. Nobody can edit it. It shows who signed, when and from where.",
      },
      {
        target: '[data-tour="contract-audit"]',
        title: "Check a copy",
        text: "At the bottom, choose a file. The page tells you if it is the exact signed copy.",
      },
    ],
  },
  {
    id: "contract-payments",
    title: "Record a payment and link its invoice",
    hint: "2 steps",
    pages: [CONTRACT],
    group: "CRM: Contracts",
    steps: [
      {
        target: '[data-tour="contract-payments"]',
        title: "Payments",
        text: "Each payment the contract sets is listed with its reference. When money arrives, record it here.",
      },
      {
        target: '[data-tour="contract-payments"]',
        title: "Link the old invoices",
        text: "If the invoices are already in your books, link them. This only connects them to the payments. Nothing is made twice.",
      },
    ],
  },
  {
    id: "contract-payment-menu",
    title: "Change a due date or create an invoice for a payment",
    hint: "2 steps",
    pages: [CONTRACT],
    group: "CRM: Contracts",
    steps: [
      {
        target: '[data-tour="contract-payments"]',
        title: "Mark received",
        text: "Mark received stays on each payment row. Press it when the money arrives.",
      },
      {
        target: '[data-tour="contract-payments"]',
        title: "The More menu",
        text: "Press More on a payment row for Change due date or Set due date, and Create invoice. Once an invoice exists, the same menu opens it.",
      },
    ],
  },
  {
    id: "contract-import",
    title: "Add a contract that is already signed",
    hint: "1 step",
    pages: [P.contracts.import],
    group: "CRM: Contracts",
    steps: [
      {
        target: '[aria-label="Existing signed contract"]',
        title: "Existing signed contract",
        text: "Fill in the client and the terms and choose the old signed PDF. That PDF is the proof, so nobody signs again.",
      },
    ],
  },
  // Settings (the CRM tabs now live on the one Settings page)
  {
    id: "settings-company",
    title: "Fill in the company's details",
    hint: "1 step",
    pages: [P.settings],
    group: "Settings",
    steps: [
      {
        target: "#settings-tab-company",
        title: "Company",
        text: "The registered name, registration number, address and directors. Contracts, quotes, invoices and the tax filings all read them, so type them once.",
      },
    ],
  },
  {
    id: "settings-payment",
    title: "Set how clients pay you",
    hint: "1 step",
    pages: [P.settings],
    group: "Settings",
    steps: [
      {
        target: "#settings-tab-payment",
        title: "Getting paid",
        text: "Your bank details and the wording clients are told to use when they pay. They print on every invoice and contract.",
      },
    ],
  },
  {
    id: "settings-sales",
    title: "Set your sales targets and rates",
    hint: "1 step",
    pages: [P.settings],
    group: "Settings",
    steps: [
      {
        target: "#settings-tab-sales",
        title: "Sales",
        text: "How many days before a lead counts as stale, your daily outreach goal and your hourly rates.",
      },
    ],
  },
  {
    id: "settings-templates",
    title: "Change the wording of your messages",
    hint: "1 step",
    pages: [P.settings],
    group: "Settings",
    steps: [
      {
        target: "#settings-tab-messages",
        title: "Messages",
        text: "The WhatsApp wording you send to clients. Change a template and the next message uses the new words.",
      },
    ],
  },
  {
    id: "settings-import",
    title: "Bring in old data",
    hint: "1 step",
    pages: [P.settings],
    group: "Settings",
    steps: [
      {
        target: "#settings-tab-imports",
        title: "Imports",
        text: "Bring in leads from a CSV file, and old contracts and invoices from your earlier tools.",
      },
    ],
  },
  {
    id: "settings-consent",
    title: "See who agreed to be contacted",
    hint: "1 step",
    pages: [P.settings],
    group: "Settings",
    steps: [
      {
        target: "#settings-tab-consent",
        title: "Consent log",
        text: "A record of who said yes or no to being contacted, and when. It protects you if anyone asks.",
      },
    ],
  },
  {
    id: "settings-books",
    title: "Finish setting up the books",
    hint: "1 step",
    pages: [P.settings],
    group: "Settings",
    steps: [
      {
        target: "#settings-tab-books",
        title: "Books setup",
        text: "Opens the books' setup steps: starting balances, the Zoho import and locking months. Until every step is done it is also in the Accounting menu.",
      },
    ],
  },
];
